import * as Sentry from '@sentry/react';

export { ErrorBoundary } from '@sentry/react';

export const captureError = (
  error: Error | string,
  context?: Record<string, any>
) => {
  Sentry.withScope(scope => {
    if (context) {
      if (context.component) {
        scope.setTag('component', context.component);
      }
      if (context.action) {
        scope.setTag('action', context.action);
      }
      if (context.userId) {
        scope.setUser({ id: context.userId });
      }
      if (context.endpoint) {
        scope.setTag('endpoint', context.endpoint);
      }
      if (context.branchId) {
        scope.setTag('branch_id', context.branchId);
      }
      if (context.productId) {
        scope.setTag('product_id', context.productId);
      }
      
      const filteredContext = { ...context };
      delete filteredContext.component;
      delete filteredContext.action;
      delete filteredContext.userId;
      delete filteredContext.endpoint;
      delete filteredContext.branchId;
      delete filteredContext.productId;
      
      if (Object.keys(filteredContext).length > 0) {
        scope.setContext('error_details', filteredContext);
      }
    }

    if (typeof error === 'string') {
      scope.setLevel('error');
      Sentry.captureMessage(error);
    } else {
      scope.setLevel('error');
      scope.setFingerprint([error.name, error.message]);
      Sentry.captureException(error);
    }
  });
};

export const captureApiError = (
  error: any,
  endpoint: string,
  method?: string,
  userId?: string
) => {
  Sentry.withScope(scope => {
    scope.setTag('error_type', 'api_error');
    scope.setTag('api_endpoint', endpoint);
    scope.setTag('http_method', method || 'unknown');
    
    if (userId) {
      scope.setUser({ id: userId });
    }

    const status = error?.response?.status;
    if (status) {
      scope.setTag('http_status', status.toString());
    }

    scope.setContext('api_details', {
      endpoint,
      method: method || 'unknown',
      status,
      statusText: error?.response?.statusText,
      requestData: error?.config?.data ? JSON.parse(error.config.data) : undefined,
      responseData: error?.response?.data
        ? {
            success: error.response.data.success,
            message: error.response.data.message,
            error: error.response.data.error,
          }
        : undefined,
    });

    scope.setFingerprint(['api_error', endpoint, status?.toString() || 'unknown']);
    Sentry.captureException(error);
  });
};

export const captureWarning = (
  message: string,
  context?: {
    component?: string;
    action?: string;
    userId?: string;
    additionalData?: Record<string, any>;
  }
) => {
  Sentry.withScope(scope => {
    scope.setLevel('warning');
    
    if (context) {
      if (context.component) {
        scope.setTag('component', context.component);
      }
      if (context.action) {
        scope.setTag('action', context.action);
      }
      if (context.userId) {
        scope.setUser({ id: context.userId });
      }
      if (context.additionalData) {
        scope.setContext('warning_details', context.additionalData);
      }
    }

    Sentry.captureMessage(message);
  });
};

export const testSentryConnection = () => {
  try {
    Sentry.withScope(scope => {
      scope.setTag('test_type', 'connection_test');
      scope.setLevel('info');
      Sentry.captureMessage('Sentry connection test - if you see this, Sentry is working correctly');
    });
  } catch (error) {
    console.error(
      'Failed to send test message to Sentry:',
      JSON.stringify(error, Object.getOwnPropertyNames(error), 2)
    );
  }
};

export const setUserContext = (user: {
  id: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser(user);
};

export const addBreadcrumb = (
  message: string,
  category: string,
  level: 'info' | 'warning' | 'error' = 'info'
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    timestamp: Date.now() / 1000,
  });
};
